/**
 * System context: the feature registry (design/2026-10-13-system-context.md).
 *
 * Every Platinum feature has one maintained description here, grouped by app
 * section (one file per section beside this one). When you add or change a
 * feature, a setting or a route, change its entry in the same PR:
 * featureCoverage.test.ts fails on a route, an admin page or a Tables control
 * with no entry, and featuresJson.test.ts fails until the export below is
 * regenerated (`yarn features:json`), which also feeds docs/features/
 * (`node scripts/help/build-docs.mjs`).
 *
 * Readers: the public /help page (shipped text, 'everyone' only), Admin →
 * Management → System context (a Manager's note per feature, laid over the
 * shipped text by mergeFeatureContext; every entry, 'internal' ones marked),
 * Douglas's guide notes (via help/sections.ts: what the person may read, never
 * 'internal'), goapi (goapi/pkg/syscontext/features.json, every entry) and
 * docs/features/ (every audience but 'internal').
 */

import type { FeatureContextRecord, HelpStartersRecord } from '../../types/gotypes'
import type { Feature, FeatureAudience, FeatureSection } from './types'
import { agentSection } from './agent'
import { tablesSection } from './tables'
import { variablesSection } from './variables'
import { datavizSection } from './dataviz'
import { storyboardsSection } from './storyboards'
import { filesSection } from './files'
import { alertsSection } from './alerts'
import { skillsSection } from './skills'
import { autocoderSection } from './autocoder'
import { themeaiSection } from './themeai'
import { programmeTrackersSection } from './programmeTrackers'
import { peopleSection } from './people'
import { configurationSection } from './configuration'
import { billingSection } from './billing'
import { operationsSection } from './operations'
import { accountSection } from './account'

export type { Feature, FeatureAudience, FeatureSection } from './types'

/** Every app section, in the order people meet them. */
export const FEATURE_SECTIONS: FeatureSection[] = [
  agentSection,
  tablesSection,
  variablesSection,
  datavizSection,
  storyboardsSection,
  filesSection,
  alertsSection,
  skillsSection,
  autocoderSection,
  themeaiSection,
  programmeTrackersSection,
  peopleSection,
  configurationSection,
  billingSection,
  operationsSection,
  accountSection,
]

/**
 * Routes in routes.tsx that no section describes, each with the reason. The
 * coverage test fails on any other undescribed route, and on an entry here
 * that a section also lists or that routes.tsx no longer has.
 */
export const UNDOCUMENTED_ROUTES: Record<string, string> = {
  admin: 'Redirects to the first admin page the person may open; it has no page of its own.',
  'admin-settings': 'An old address that redirects to Change Password.',
  'agent-artifacts': 'A stub page with an empty list; nothing links to it. Files are viewed inside the session.',
  'agent-design': 'A design showcase of every message shape, for the team changing the chat’s look; not a customer feature.',
  developer: 'Developer tools for the team; nothing in the app links to it.',
  visualizations: 'An old demo chart slideshow; reached only from the other demo pages.',
  'visualizations-real': 'An old demo page (women’s football); reached only from the other demo pages.',
  'tiktok-comments': 'An old TikTok demo page; reached only from the other demo pages.',
  'tiktok-analytics': 'An old TikTok demo page; reached only from the other demo pages.',
  'tiktok-start': 'An old TikTok demo page; reached only from the other demo pages.',
  'tiktok-dashboard': 'An old TikTok demo page; reached only from the other demo pages.',
}

/** The section whose routes list this route name (routes.tsx), if any. The Help pane reads it for the page on screen. */
export function sectionForRoute(routeName: string, sections: FeatureSection[] = FEATURE_SECTIONS): FeatureSection | undefined {
  return sections.find((s) => s.routes.includes(routeName))
}

/** A feature's audience: its own, else its section's. */
export function featureAudience(section: FeatureSection, feature: Feature): FeatureAudience {
  return feature.audience ?? section.audience
}

/**
 * Who is reading. 'public' is anyone, signed in or not; 'realm' a Realm admin
 * (the customer's own admin); 'manager' a Manager (Bayes Price staff). Nobody
 * reads 'internal' entries.
 */
export type HelpReader = 'public' | 'realm' | 'manager'

const READS: Record<HelpReader, FeatureAudience[]> = {
  public: ['everyone'],
  realm: ['everyone', 'realm'],
  manager: ['everyone', 'realm', 'manager'],
}

/** The audiences from most open to most closed. A section's is the most open of its features'. */
export const AUDIENCE_ORDER: FeatureAudience[] = ['everyone', 'realm', 'manager', 'internal']

/** The reader a signed-in person is, from their role permissions. A Manager is also a Realm admin. */
export function helpReaderFor(roles?: { isManager?: boolean; isRealmAdmin?: boolean } | null): HelpReader {
  if (roles?.isManager) return 'manager'
  if (roles?.isRealmAdmin) return 'realm'
  return 'public'
}

/** True when this reader may see this audience. 'internal' is never read. */
export function canRead(reader: HelpReader, audience: FeatureAudience): boolean {
  return READS[reader].includes(audience)
}

/** The sections and features this reader may see, in registry order. */
export function sectionsFor(reader: HelpReader, sections: FeatureSection[] = FEATURE_SECTIONS): FeatureSection[] {
  return sections
    .filter((s) => canRead(reader, s.audience))
    .map((s) => ({ ...s, features: s.features.filter((f) => canRead(reader, featureAudience(s, f))) }))
}

/**
 * What everyone may read: 'everyone' sections, without their other
 * features. The public /help page shows this and nothing else.
 */
export function publicSections(sections: FeatureSection[] = FEATURE_SECTIONS): FeatureSection[] {
  return sectionsFor('public', sections)
}

/**
 * The registry as goapi/pkg/syscontext/features.json holds it: every field,
 * each feature's audience resolved, keys in a fixed order so the file only
 * changes when the content does.
 */
export function toFeaturesJson(sections: FeatureSection[] = FEATURE_SECTIONS) {
  return {
    sections: sections.map((s) => ({
      key: s.key,
      title: s.title,
      ...(s.navPage ? { navPage: s.navPage } : {}),
      routes: [...s.routes],
      audience: s.audience,
      about: s.about,
      ...(s.steps?.length ? { steps: [...s.steps] } : {}),
      ...(s.starters?.length ? { starters: [...s.starters] } : {}),
      ...(s.intro ? { intro: s.intro } : {}),
      ...(s.image ? { image: s.image, imageAlt: s.imageAlt ?? '' } : {}),
      features: s.features.map((f) => ({
        key: f.key,
        name: f.name,
        what: f.what,
        how: f.how,
        ...(f.controls?.length ? { controls: [...f.controls] } : {}),
        ...(f.tools?.length ? { tools: [...f.tools] } : {}),
        ...(f.settings?.length ? { settings: [...f.settings] } : {}),
        ...(f.agentNotes ? { agentNotes: f.agentNotes } : {}),
        ...(f.image ? { image: f.image, imageAlt: f.imageAlt ?? '' } : {}),
        audience: featureAudience(s, f),
      })),
    })),
  }
}

/** A Manager's note on a feature, as shown beside the shipped text. */
export interface FeatureNote {
  text: string
  by: string
  /** Unix seconds. */
  at: number
}

export type FeatureWithContext = Feature & { context?: FeatureNote }
export type SectionWithContext = Omit<FeatureSection, 'features'> & { features: FeatureWithContext[] }

/**
 * A customer's notes laid over the shipped registry. Each note attaches to
 * its feature by (section key, feature key); the shipped text is never
 * replaced, so a change to it reaches the customer at once. A note whose key
 * no longer ships is dropped here (the server refuses new ones), and a
 * job-level note is ignored until job-level notes exist.
 */
export function mergeFeatureContext(sections: FeatureSection[], notes: FeatureContextRecord[]): SectionWithContext[] {
  const byKey = new Map(notes.filter((n) => !n.job).map((n) => [`${n.section_key}/${n.feature_key}`, n]))
  return sections.map((s) => ({
    ...s,
    features: s.features.map((f) => {
      const n = byKey.get(`${s.key}/${f.key}`)
      return n ? { ...f, context: { text: n.context, by: n.updated_by, at: n.updated_at } } : f
    }),
  }))
}

/** The most starter questions a section holds, and the longest one (the server refuses more). */
export const MAX_HELP_STARTERS = 6
export const MAX_HELP_STARTER_CHARS = 200

/**
 * The starter questions a section's page helpers and the Help pane offer
 * before the first message: the customer's own list when they saved one
 * (even an empty one, which means none), else the shipped `starters`.
 * `custom` says the customer's list applies (Reset is then possible). A
 * job-level row is ignored until job-level lists exist.
 */
export function starterQuestionsFor(
  sectionKey: string,
  records: HelpStartersRecord[],
  sections: FeatureSection[] = FEATURE_SECTIONS,
): { questions: string[]; custom: boolean } {
  const own = records.find((r) => !r.job && r.section_key === sectionKey)
  if (own) return { questions: [...(own.questions ?? [])], custom: true }
  return { questions: [...(sections.find((s) => s.key === sectionKey)?.starters ?? [])], custom: false }
}

/** The longest opening line (the server refuses more), and what a section without one says. */
export const MAX_HELP_INTRO_CHARS = 200
export const DEFAULT_SECTION_INTRO = 'What would you like to do today?'

/**
 * The opening line a section's page helpers and the Help pane say after the
 * greeting: the customer's own line when they saved a non-empty one, else the
 * shipped `intro`, else DEFAULT_SECTION_INTRO. `custom` says the customer's
 * line applies. A job-level row is ignored, as for the questions.
 */
export function sectionIntroFor(
  sectionKey: string,
  records: HelpStartersRecord[],
  sections: FeatureSection[] = FEATURE_SECTIONS,
): { intro: string; shipped: string; custom: boolean } {
  const shipped = sections.find((s) => s.key === sectionKey)?.intro || DEFAULT_SECTION_INTRO
  const own = records.find((r) => !r.job && r.section_key === sectionKey)?.intro?.trim()
  return own ? { intro: own, shipped, custom: true } : { intro: shipped, shipped, custom: false }
}

/**
 * One section as plain text: its purpose, then each feature with what it is
 * for, how to use it, its settings, what an agent needs told, and the
 * customer's note. The shape the agent block (System context PR B) reads.
 */
export function renderSectionNotes(section: SectionWithContext | FeatureSection): string {
  const lines = [`## ${section.title}`, section.about]
  for (const f of section.features as FeatureWithContext[]) {
    lines.push(`- ${f.name}: ${f.what} ${f.how}`)
    if (f.settings?.length) lines.push(`  Settings: ${f.settings.join('; ')}`)
    if (f.agentNotes) lines.push(`  Agent notes: ${f.agentNotes}`)
    if (f.context) lines.push(`  This customer: ${f.context.text}`)
  }
  if (section.steps?.length) lines.push(`Typical route: ${section.steps.map((st, i) => `${i + 1}. ${st}`).join(' ')}`)
  return lines.join('\n')
}

/** The page warns past this many characters: the agent block's cap per note (PR B). */
export const FEATURE_CONTEXT_WARN_CHARS = 4000
